/*@flow*/
import assert from './assert';
import getTypeName from './getTypeName';
import isIdentity from './isIdentity';
import create from './create';
import is from './is';
import forbidNewOperator from './forbidNewOperator';
import isUnion from './isUnion';
import isNil from './isNil';
import update from './update';
import type { TypeT } from './types';
import withOptions from './withOptions';

type UnionType = TypeT<{ types: TypeT<*>[] }>;

function getDefaultName(types: TypeT<*>[]): string {
  return types.map(getTypeName).join(' | ');
}

function union(types: TypeT<*>[], name?: string): UnionType {
  if (process.env.NODE_ENV !== 'production') {
    if (types.length < 2) {
      throw new Error(
        'Invalid argument types ' +
          assert.stringify(types) +
          ' supplied to union(types, [name]) combinator (expected an array of at least 2 types)'
      );
    }
  }

  let displayName = name || getDefaultName(types);
  let identity = types.every(isIdentity);

  function Union(value: any, path?: string[]) {
    if (process.env.NODE_ENV === 'production') {
      if (identity) return value;
    }

    let type = Union.dispatch(value);

    if (!type) {
      return value;
    }

    if (process.env.NODE_ENV !== 'production') {
      if (identity) forbidNewOperator(this, Union);
      path = path || [displayName];
      path[path.length - 1] += '(' + getTypeName(type) + ')';
    }

    if (this && this.meta && this.meta.options) {
      type = type.opt(this.meta.options);
    }

    return create(type, value, path);
  }

  Union.meta = {
    kind: 'union',
    types,
    name,
    identity,
  };

  Union.displayName = displayName;

  Union.is = (x: any): %checks => {
    return types.some((type: TypeT<*>) => is(x, type));
  };

  Union.dispatch = (x: any): ?TypeT<*> => {
    let ret;
    types.forEach(type => {
      if (isUnion(type)) {
        // handle union of unions
        let t = type.dispatch(x);
        if (!isNil(t)) {
          ret = t;
        }
      } else if (is(x, type)) {
        ret = type;
      }
    });
    return ret;
  };

  Union.update = (instance: UnionType, patch: {}): UnionType => {
    return Union(update<UnionType>(instance, patch));
  };

  withOptions(Union);

  return Union;
}

union.getDefaultName = getDefaultName;

export default union;
